import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  signal,
} from "@angular/core";
import { TranslocoPipe } from "@jsverse/transloco";
import { KsgButton, KsgIcon } from "@ksg/ui";
import { TourService } from "./tour.service";

interface BoxStyle {
  top: string;
  left: string;
  width: string;
  height: string;
}

const CARD_WIDTH = 340;

@Component({
  selector: "app-tour-overlay",
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [TranslocoPipe, KsgButton, KsgIcon],
  host: {
    "(window:resize)": "locate()",
    "(window:scroll)": "locate()",
    "(document:keydown)": "onKeydown($event)",
  },
  templateUrl: "./tour-overlay.html",
  styleUrl: "./tour-overlay.scss",
})
export class TourOverlay {
  readonly service = inject(TourService);

  /** Prostokąt celu (fixed/viewport). Aktualizowany przy zmianie kroku, scrollu, resize. */
  private readonly rect = signal<DOMRect | null>(null);

  readonly highlight = computed<BoxStyle | null>(() => {
    const r = this.rect();
    if (!r) {
      return null;
    }
    return {
      top: `${r.top - 4}px`,
      left: `${r.left - 4}px`,
      width: `${r.width + 8}px`,
      height: `${r.height + 8}px`,
    };
  });

  readonly cardStyle = computed<{ top: string; left: string }>(() => {
    const r = this.rect();
    if (typeof window === "undefined" || !r) {
      return { top: "50%", left: `calc(50% - ${CARD_WIDTH / 2}px)` };
    }
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const left = Math.min(Math.max(r.left, 12), vw - CARD_WIDTH - 12);
    const below = r.bottom + 12;
    const top = below + 220 > vh ? Math.max(r.top - 232, 12) : below;
    return { top: `${top}px`, left: `${Math.max(left, 12)}px` };
  });

  constructor() {
    effect(() => {
      this.service.currentStep();
      this.service.stepIndex();
      queueMicrotask(() => this.locate());
    });
  }

  locate(): void {
    const step = this.service.currentStep();
    if (typeof document === "undefined" || !step?.selector) {
      this.rect.set(null);
      return;
    }
    const el = document.querySelector<HTMLElement>(step.selector);
    if (!el) {
      this.rect.set(null);
      return;
    }
    el.scrollIntoView({ block: "center", inline: "nearest" });
    this.rect.set(el.getBoundingClientRect());
  }

  onKeydown(event: KeyboardEvent): void {
    if (!this.service.activeTour()) {
      return;
    }
    if (event.key === "Escape") {
      event.preventDefault();
      this.service.stop();
    } else if (event.key === "ArrowRight") {
      event.preventDefault();
      this.service.next();
    } else if (event.key === "ArrowLeft") {
      event.preventDefault();
      this.service.prev();
    }
  }
}
